import { InteractionEventTrigger, useViewTracking } from '@blueshift-ui/telemetry';
import { isToday, isTomorrow } from 'date-fns';

import { Button } from '@llt-frontend/react-material-button';
import { Icon } from '@llt-frontend/react-material-icon';
import NotificationCard from '../messaging/notification-card';
import PropTypes from 'prop-types';
import React from 'react';
import { format } from 'date-fns-tz';
import useInterval from '../../hooks/utility/use-interval';
import { useTranslation } from 'react-i18next';

//

const ANALYTICS_CATEGORY = 'day-orchestrator';
const MILLISECONDS_IN_A_MINUTE = 60_000;
const VIEWED_SCHEDULE_ANALYTICS = {
  action: 'viewed-schedule',
  category: ANALYTICS_CATEGORY,
};

//

function _getEventDate(event, translate) {
  const startTime = new Date(event.startTime);

  if (isToday(startTime)) {
    return translate('today');
  }

  if (isTomorrow(startTime)) {
    return translate('tomorrow');
  }

  return format(startTime, 'MMM do');
}

function _getEventDuration(event) {
  const startTime = new Date(event.startTime);
  const endTime = new Date(event.endTime);
  const timeFormat = "h:mmaaaaa'm'";
  return `${format(startTime, timeFormat)} - ${format(endTime, `${timeFormat} zzz`)}`;
}

function _getMinutesRemaining({ endTime }) {
  const millisecondsRemaining = Date.parse(endTime) - Date.now();
  return Math.floor(millisecondsRemaining / MILLISECONDS_IN_A_MINUTE);
}

function _getMotionId(event) {
  if (!event) {
    return 'no-event-transition';
  }

  return `${event.occurring() ? 'current' : 'future'}-event-${event.startTime}-transition`;
}

//

function _JoinButton({ children, event, translate }) {
  const eventOccurring = event.occurring();

  //
  // Side-Effects

  useViewTracking({
    ...VIEWED_SCHEDULE_ANALYTICS,
    label:
      eventOccurring && !event.joinUrl
        ? 'missing-join-url'
        : `${eventOccurring ? 'current' : 'future'}-activity`,
  });

  //
  // Render

  if (!eventOccurring) {
    return <div className="scheduled-event__join-placeholder">{translate('joinPlaceholder')}</div>;
  }

  if (!event.joinUrl) {
    return (
      <div className="scheduled-event__join-placeholder">
        {`${translate('missingLink')} `}
        <a href="mailto:help@varsitytutors.com">help@varsitytutors.com</a>
      </div>
    );
  }

  return (
    <InteractionEventTrigger
      analytics={{
        action: 'clicked-join-link',
        category: ANALYTICS_CATEGORY,
        label: event.type,
      }}
      element={Button}
      elementProps={{
        contained: true,
        href: event.joinUrl,
        target: '_blank',
      }}
    >
      {children}
    </InteractionEventTrigger>
  );
}

_JoinButton.propTypes = {
  children: PropTypes.node.isRequired,
  event: PropTypes.shape({
    joinUrl: PropTypes.string,
    occurring: PropTypes.func.isRequired,
  }).isRequired,
  translate: PropTypes.func.isRequired,
};

//

function _TimeRemaining({ event, translate }) {
  if (!event.occurring()) {
    return null;
  }

  const minutesRemaining = _getMinutesRemaining(event);

  return (
    <p className="scheduled-event__time-remaining">
      {translate(minutesRemaining > 1 ? 'minutesRemaining' : 'minuteRemaining').replace(
        '{minutes}',
        minutesRemaining
      )}
    </p>
  );
}

_TimeRemaining.propTypes = {
  event: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
};

//

function ScheduledEventNotification({ event }) {
  const { ready: translationLoaded, t: translate } = useTranslation('day-orchestrator/index');

  //
  // Side-Effects

  useInterval(MILLISECONDS_IN_A_MINUTE / 4); // Rerender every 15 seconds

  //
  // Render

  if (!translationLoaded || !event) {
    return null;
  }

  return (
    <NotificationCard
      action={
        <React.Fragment>
          <_JoinButton {...{ event, translate }}>{translate('join')}</_JoinButton>

          <_TimeRemaining {...{ event, translate }} />
        </React.Fragment>
      }
      heading={
        <span className="scheduled-event__occurs-at">
          <Icon>schedule</Icon>
          <b className="scheduled-event__date">{_getEventDate(event, translate)}</b>
          {'@'}
          <span className="scheduled-event__duration">{_getEventDuration(event)}</span>
        </span>
      }
      motionId={_getMotionId(event)}
    >
      {event.name}
    </NotificationCard>
  );
}

ScheduledEventNotification.propTypes = {
  event: PropTypes.shape({
    endTime: PropTypes.string.isRequired,
    joinUrl: PropTypes.string,
    occurring: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
  }),
};

// Exports
// -------

export default ScheduledEventNotification;
